import { useState, useEffect, useRef } from 'react';
import { trpc } from '../api/trpc';
import { useDialogClose } from '../hooks/useDialogClose';
import type { ConversationListItem, ConversationSearchResultItem } from '@cade/api-client';

interface Props {
  onSelect: (conversationId: string) => void;
  onClose: () => void;
}

const DEBOUNCE_MS = 200;

// Render a ts_headline snippet that may contain literal <mark>…</mark>
// tags. Everything else is escaped — we only trust the mark wrapping
// emitted by Postgres ts_headline (StartSel=<mark>,StopSel=</mark> per
// services/conversation/src/repository.ts); the original message content
// is untrusted. Returns `{__html: ''}` for null so callers can pass the
// result unconditionally to dangerouslySetInnerHTML without React warnings.
// If the SQL StartSel/StopSel ever change, update this sanitizer in lockstep.
export function renderSnippet(snippet: string | null): { __html: string } {
  if (!snippet) return { __html: '' };
  const escaped = snippet
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  // Re-allow only the literal <mark> / </mark> markers ts_headline emitted.
  const restored = escaped
    .replace(/&lt;mark&gt;/g, '<mark>')
    .replace(/&lt;\/mark&gt;/g, '</mark>');
  return { __html: restored };
}


export function SearchModal({ onSelect, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { isClosing, handleClose } = useDialogClose(onClose);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleClose]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Empty-query state: fall back to the conversation list (most-recent first).
  const { data: list } = trpc.conversations.list.useQuery(
    {},
    { enabled: debouncedQuery.length === 0 },
  );

  const { data: results, isFetching } = trpc.conversations.search.useQuery(
    { query: debouncedQuery, limit: 30 },
    { enabled: debouncedQuery.length >= 1, staleTime: 30_000 },
  );

  const isLoading = debouncedQuery.length >= 1 && isFetching;

  return (
    <div className={`search-modal-overlay${isClosing ? ' overlay-closing' : ''}`} onClick={handleClose}>
      <div className={`search-modal${isClosing ? ' dialog-closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="search-modal-input-wrap">
          {isLoading ? (
            <svg className="search-modal-icon search-modal-spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-label="Searching">
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" strokeDasharray="9 5" strokeLinecap="round" />
            </svg>
          ) : (
            <svg className="search-modal-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
              <line x1="10.5" y1="10.5" x2="14" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
          <input
            ref={inputRef}
            className="search-modal-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats…"
          />
          <kbd className="search-modal-kbd">ESC</kbd>
        </div>
        <div className="search-modal-results">
          {debouncedQuery.length >= 1 ? (
            (results ?? []).length === 0 && !isLoading ? (
              <div className="search-modal-empty">No chats match</div>
            ) : (
              (results ?? []).map((r: ConversationSearchResultItem) => (
                <button
                  key={r.conversationId}
                  className="search-modal-result"
                  onClick={() => { onSelect(r.conversationId); onClose(); }}
                >
                  <span className="search-modal-result-row">
                    {r.titleSnippet ? (
                      <span
                        className="search-modal-result-title"
                        dangerouslySetInnerHTML={renderSnippet(r.titleSnippet)}
                      />
                    ) : (
                      <span className="search-modal-result-title">{r.title ?? 'New chat'}</span>
                    )}
                    <span className="search-modal-result-date">{new Date(r.lastActivityAt).toLocaleDateString()}</span>
                  </span>
                  {r.messageSnippet ? (
                    <span
                      className="search-modal-result-snippet"
                      dangerouslySetInnerHTML={renderSnippet(r.messageSnippet)}
                    />
                  ) : null}
                </button>
              ))
            )
          ) : (
            (list ?? []).length === 0 ? (
              <div className="search-modal-empty">No chats found</div>
            ) : (
              (list ?? []).map((conv: ConversationListItem) => {
                const hasUnread = (conv.unreadCount ?? 0) > 0;
                return (
                  <button
                    key={conv.id}
                    className={`search-modal-result ${hasUnread ? 'unread' : ''}`}
                    onClick={() => { onSelect(conv.id); onClose(); }}
                  >
                    <span className="search-modal-result-row">
                      <span className="search-modal-result-title">{conv.title ?? 'New chat'}</span>
                      {hasUnread
                        ? <span className="sb-unread-badge">{conv.unreadCount! > 10 ? '10+' : conv.unreadCount}</span>
                        : <span className="search-modal-result-date">{new Date(conv.updatedAt).toLocaleDateString()}</span>
                      }
                    </span>
                  </button>
                );
              })
            )
          )}
        </div>
      </div>
    </div>
  );
}
